import React, { useState } from 'react';
import { CheckPicker, InputNumber, Message } from 'rsuite';
import DatePicker from '../shared/DatePicker';
import { Button, Modal } from '@nsi-advisory-services/components';
import { useSaveSurveySchedule, useStopSurveySchedule, useSurveyPeople } from '../../hooks/useSurveys';
import { formatDate, scheduleLabel } from '../../lib/surveys';
import { showToast } from '../../utils/toast';

const PACES = [
  { value: 1, label: 'Every month' },
  { value: 3, label: 'Every quarter' },
  { value: 12, label: 'Every year' },
  { value: 'custom', label: 'Every N months' },
];

const MAX_EVERY_MONTHS = 60;
const DEFAULT_CUSTOM_MONTHS = 6;
const DEFAULT_RESPONSE_DAYS = 14;

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const fromIsoDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const whole = (value) => {
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
};

/**
 * Repeats a survey: how often, from when, who each round goes to and how long it stays open.
 * Each round is a new survey made from this one as saved at the time.
 */
export default function SurveyScheduleModal({ survey, onClose }) {
  const peopleQuery = useSurveyPeople();
  const saveSchedule = useSaveSurveySchedule();
  const stopSchedule = useStopSurveySchedule();
  const existing = survey.schedule;
  const today = toIsoDate(new Date());

  const startingMonths = existing?.everyMonths || 1;
  const [pace, setPace] = useState(PACES.some((p) => p.value === startingMonths) ? startingMonths : 'custom');
  const [customMonths, setCustomMonths] = useState(PACES.some((p) => p.value === startingMonths) ? DEFAULT_CUSTOM_MONTHS : startingMonths);
  const [startsOn, setStartsOn] = useState(existing?.nextRunOn && existing.nextRunOn > today ? existing.nextRunOn : today);
  const [responseDays, setResponseDays] = useState(existing?.responseDays || DEFAULT_RESPONSE_DAYS);
  const [audience, setAudience] = useState(existing?.audience || 'all');
  const [recipientIds, setRecipientIds] = useState(existing?.recipientIds || []);
  const [error, setError] = useState('');

  const everyMonths = pace === 'custom' ? whole(customMonths) : pace;
  const days = whole(responseDays);
  const people = peopleQuery.data || [];
  const busy = saveSchedule.isPending || stopSchedule.isPending;
  const ready = everyMonths >= 1 && everyMonths <= MAX_EVERY_MONTHS && Boolean(startsOn) && days >= 1
    && (audience === 'all' || recipientIds.length > 0);

  const save = async () => {
    setError('');
    try {
      await saveSchedule.mutateAsync({
        id: survey.id,
        everyMonths,
        startsOn,
        responseDays: days,
        audience,
        recipientIds: audience === 'selected' ? recipientIds : [],
      });
      showToast('success', startsOn === today ? 'Schedule saved. The first round goes out today.' : `Schedule saved. The next round goes out ${formatDate(startsOn)}.`);
      onClose();
    } catch (failure) {
      setError(String(failure?.message || failure || 'Unable to save the schedule.'));
    }
  };

  const stop = async () => {
    setError('');
    try {
      await stopSchedule.mutateAsync(survey.id);
      showToast('success', 'This survey no longer repeats. Rounds already out carry on.');
      onClose();
    } catch (failure) {
      setError(String(failure?.message || failure || 'Unable to stop the schedule.'));
    }
  };

  return (
    <Modal open onClose={busy ? undefined : onClose} size="sm" overflow backdrop={busy ? 'static' : true} keyboard={!busy}>
      <Modal.Header closeDisabled={busy}>
        <Modal.Title>Repeat this survey</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h3 className="svb-send__title">{survey.title}</h3>
        {error || peopleQuery.error ? <Message type="error" showIcon>{error || String(peopleQuery.error?.message || peopleQuery.error)}</Message> : null}

        <div className="svb-field">
          <span className="bdc-modal__label">How often</span>
          <div className="svb-segment" role="radiogroup" aria-label="How often">
            {PACES.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={pace === option.value}
                className={`svb-segment__option${pace === option.value ? ' is-active' : ''}`}
                onClick={() => setPace(option.value)}
                disabled={busy}
              >
                {option.label}
              </button>
            ))}
          </div>
          {pace === 'custom' ? (
            <label className="svb-schedule__number">
              <span>Every</span>
              <InputNumber min={1} max={MAX_EVERY_MONTHS} value={customMonths} onChange={setCustomMonths} disabled={busy} aria-label="Months between rounds" />
              <span>months</span>
            </label>
          ) : null}
        </div>

        <div className="svb-field">
          <span className="bdc-modal__label">{existing ? 'Next round goes out' : 'First round goes out'}</span>
          <DatePicker
            block
            oneTap
            cleanable={false}
            format="MM/dd/yyyy"
            value={fromIsoDate(startsOn)}
            onChange={(value) => setStartsOn(toIsoDate(value))}
            shouldDisableDate={(date) => toIsoDate(date) < today}
            disabled={busy}
            aria-label="Next round date"
          />
          <span className="svb-hint">Rounds go out from 8am company time on the day.</span>
        </div>

        <div className="svb-field">
          <span className="bdc-modal__label">Each round stays open</span>
          <label className="svb-schedule__number">
            <InputNumber min={1} max={365} value={responseDays} onChange={setResponseDays} disabled={busy} aria-label="Days each round stays open" />
            <span>days</span>
          </label>
        </div>

        <div className="svb-field">
          <span className="bdc-modal__label">Who each round goes to</span>
          <div className="svb-segment" role="radiogroup" aria-label="Who each round goes to">
            {[
              { value: 'all', label: 'All Employees' },
              { value: 'selected', label: 'Selected people' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={audience === option.value}
                className={`svb-segment__option${audience === option.value ? ' is-active' : ''}`}
                onClick={() => setAudience(option.value)}
                disabled={busy}
              >
                {option.label}
              </button>
            ))}
          </div>
          {audience === 'selected' ? (
            <CheckPicker
              block
              data={people.map((person) => ({ value: person.id, label: person.name, email: person.email }))}
              value={recipientIds}
              onChange={(ids) => setRecipientIds(ids || [])}
              disabled={peopleQuery.isLoading || busy}
              placeholder="Pick people"
              aria-label="Recipients"
              searchBy={(keyword, label, person) => `${label} ${person.email || ''}`.toLowerCase().includes(keyword.toLowerCase())}
              countable
            />
          ) : null}
          <span className="svb-hint">
            {audience === 'all' ? 'Everyone active on the day, including people who join later.' : 'Anyone no longer active on the day is left out.'}
          </span>
        </div>

        <p className="svb-hint">
          {everyMonths ? `${scheduleLabel(everyMonths)}, a new copy of this survey as saved at the time is sent out and emailed. ` : ''}
          Each round has its own responses and reminders. Changes here apply to later rounds, not ones already out.
        </p>
      </Modal.Body>
      <Modal.Footer>
        {existing ? (
          <Button variant="danger" onClick={stop} loading={stopSchedule.isPending} disabled={busy}>Stop repeating</Button>
        ) : null}
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saveSchedule.isPending} disabled={busy || !ready}>Save schedule</Button>
      </Modal.Footer>
    </Modal>
  );
}
